import { usePageLayoutResize } from '@atlaskit/page-layout';

import { BREADCRUMBS_MODES } from '@confluence/breadcrumbs';
import { fg } from '@confluence/feature-gating';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore';
import { getIsSideNavCollapsed } from '@confluence/page-layout/entry-points/getIsSideNavCollapsed';

type BreadcrumbsProps = {
	contentType: string;
	contentMode: string;
	isSpaceOverviewPage: boolean;
	isArchived: boolean;
	isTitleVisible?: boolean;
};

export const useBreadcrumbsDisplayMode = ({
	contentType,
	contentMode,
	isSpaceOverviewPage,
	isArchived,
	isTitleVisible = false, // placeholder value for now. To be tied in with hook produced from COMMENTS-3322-page-title-visibility-hook
}: BreadcrumbsProps): BREADCRUMBS_MODES => {
	const { isLeftSidebarCollapsed: isNav3LeftSidebarCollapsed } = usePageLayoutResize();
	const isNav4LeftSideNavCollapsed = getIsSideNavCollapsed();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isEditorToolbarShown = contentMode === 'draft' || contentMode === 'edit' || isLiveEditMode;
	const isLeftSideNavCollapsed = isNav4LeftSideNavCollapsed || isNav3LeftSidebarCollapsed;

	if (fg('confluence_frontend_progressive_header')) {
		// all live pages, page, blogs, and editor except for space overview and archived pages
		if (
			(contentType === 'blogpost' || contentType === 'page') &&
			!isSpaceOverviewPage &&
			!isArchived
		) {
			if (isEditorToolbarShown) {
				if (isLeftSideNavCollapsed) {
					return BREADCRUMBS_MODES.FULL;
				} else {
					return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
				}
			} else {
				// title visibility only matters when there is no editor toolbar (i.e. view page or blogpost)
				if (isLeftSideNavCollapsed) {
					if (isTitleVisible) {
						return BREADCRUMBS_MODES.ONLY_SPACE_NAME;
					} else {
						return BREADCRUMBS_MODES.FULL;
					}
				} else {
					if (isTitleVisible) {
						return BREADCRUMBS_MODES.HIDDEN;
					} else {
						return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
					}
				}
			}
			// all other content types
		} else if (
			contentType === 'database' ||
			contentType === 'whiteboard' ||
			contentType === 'embed'
		) {
			if (isLeftSideNavCollapsed) {
				return BREADCRUMBS_MODES.FULL;
			} else {
				return BREADCRUMBS_MODES.ONLY_CONTENT_TITLE;
			}
		}
	}

	return BREADCRUMBS_MODES.FULL;
};
